import { memo, useEffect, useRef, useState, useCallback } from 'react';
import { useDebounce } from 'use-debounce';
import classNames from 'classnames';
import { V2QsTextareaProps } from './types';

/**
 * V2QsTextarea 컴포넌트
 *
 * 장문 텍스트 입력을 위한 컴포넌트
 * V1 QsTextarea의 V2 버전으로 순수 UI만 담당
 *
 * @example
 * ```tsx
 * <V2QsTextarea
 *   value={description}
 *   onChange={setDescription}
 *   placeholder="Enter description"
 *   maxLength={500}
 * />
 * ```
 */
function V2QsTextarea({
  value = '',
  onChange,
  debounceMs = 300,
  placeholder = 'Enter text',
  minLength,
  maxLength,
  showCharCount = true,
  autoResize = true,
  minRows = 3,
  maxRows,
  error = false,
  errorMessage,
  disabled = false,
  readOnly = false,
  fullWidth = true,
  className,
  containerClassName,
  label,
  hint,
  autoFocus = false,
}: V2QsTextareaProps) {
  const [inputValue, setInputValue] = useState(value);
  const [debouncedValue] = useDebounce(inputValue, debounceMs);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const initialValueRef = useRef(value);

  // 외부 value 변경 시 동기화 (초기값과 다를 때만)
  useEffect(() => {
    if (initialValueRef.current !== value) {
      initialValueRef.current = value;
      setInputValue(value);
    }
  }, [value]);

  // 디바운스된 값이 변경되면 onChange 호출
  useEffect(() => {
    if (debouncedValue !== value) {
      onChange?.(debouncedValue);
    }
  }, [debouncedValue, onChange, value]);

  // 자동 포커스
  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [autoFocus]);

  // 자동 높이 조절
  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea || !autoResize) return;

    // 높이 리셋 후 scrollHeight로 조절
    textarea.style.height = 'auto';

    // minRows에 해당하는 최소 높이 계산 (line-height 24px 기준)
    const lineHeight = 24;
    const padding = 34; // py-[17px] * 2
    const minHeight = lineHeight * minRows + padding;

    // maxRows가 있으면 최대 높이 제한
    const maxHeight = maxRows ? lineHeight * maxRows + padding : Infinity;

    const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
    textarea.style.height = `${newHeight}px`;
  }, [autoResize, minRows, maxRows]);

  // 값 변경 시 높이 조절
  useEffect(() => {
    adjustHeight();
  }, [inputValue, adjustHeight]);

  // 값 변경 핸들러
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let newValue = e.target.value;

    // maxLength가 설정된 경우 명시적으로 글자수 제한 (복사-붙여넣기 대응)
    if (maxLength !== undefined && newValue.length > maxLength) {
      newValue = newValue.slice(0, maxLength);
    }

    setInputValue(newValue);
  };

  // 현재 글자 수
  const charCount = inputValue.length;
  const isAtLimit = maxLength !== undefined && charCount >= maxLength;
  const isBelowMin = minLength !== undefined && charCount > 0 && charCount < minLength;
  const hasCharLimit = minLength !== undefined || maxLength !== undefined;
  const hasError = error || isBelowMin;

  // Container 클래스
  const containerClasses = classNames('flex flex-col gap-1', containerClassName);

  // Textarea wrapper 클래스
  const wrapperClasses = classNames(
    'flex items-start transition-all duration-200 rounded-[10px]',
    {
      // Width
      'w-full': fullWidth,
      'w-[260px]': !fullWidth,

      // Padding
      'px-5 py-[17px]': true,

      // Background
      'bg-white': !disabled,
      'bg-[#F2F4FC]': disabled,

      // Border
      'border-[1.5px]': true,
      'border-[#E6E9F5]': !hasError && !disabled,
      'border-[#FF5E71]': hasError,
      'hover:border-[#DADDEB]': !disabled && !hasError,
      'focus-within:border-[#DADDEB]': !disabled && !hasError,
      'focus-within:bg-[#FAFBFF]': !disabled && !hasError,

      // Cursor
      'cursor-not-allowed': disabled,
    }
  );

  // Textarea 클래스
  const textareaClasses = classNames(
    'w-full outline-none bg-transparent resize-none',
    'text-[15px] font-medium leading-[1.6]',
    {
      'text-[#1D1D1F]': !disabled,
      'text-[#C3C7D9]': disabled,
      'placeholder:text-[#AFB2C2]': true,
      'placeholder:uppercase': true,
      'cursor-not-allowed': disabled,
    },
    className
  );

  // 라벨 클래스
  const labelClasses = 'text-sm font-medium text-[#222638] mb-1';

  // 힌트 클래스
  const hintClasses = 'text-xs text-[#AFB2C2]';

  // 에러 메시지 클래스
  const errorClasses = 'text-xs text-[#FF5E71]';

  // 글자 수 클래스
  const charCountClasses = classNames('text-xs', {
    'text-[#AFB2C2]': !isAtLimit && !isBelowMin,
    'text-[#FF5E71]': isAtLimit || isBelowMin,
  });

  // 글자 수 표시 문자열 생성
  // Kathy 요청: "13/300" 형식으로 표시 (공백 없이, maxLength만 표시)
  const getCharCountDisplay = () => {
    if (maxLength !== undefined) {
      return `${charCount}/${maxLength}`;
    }
    if (minLength !== undefined) {
      return `${charCount} (min: ${minLength})`;
    }
    return `${charCount}`;
  };

  return (
    <div className={containerClasses}>
      {/* Label */}
      {label && <label className={labelClasses}>{label}</label>}

      {/* Textarea Wrapper */}
      <div className={wrapperClasses}>
        <textarea
          ref={textareaRef}
          className={textareaClasses}
          value={inputValue}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          maxLength={maxLength}
          rows={minRows}
        />
      </div>

      {/* Bottom row: Hint/Error + Char Count */}
      <div className="flex justify-between items-center">
        <div>
          {/* Hint */}
          {hint && !hasError && <span className={hintClasses}>{hint}</span>}

          {/* Error Message */}
          {hasError && errorMessage && (
            <span className={errorClasses}>{errorMessage}</span>
          )}
        </div>

        {/* Char Count */}
        {showCharCount && hasCharLimit && (
          <span className={charCountClasses}>
            {getCharCountDisplay()}
          </span>
        )}
      </div>
    </div>
  );
}

export default memo(V2QsTextarea);
